import {
  createFileRoute,
  stripSearchParams,
  type ErrorComponentProps,
} from '@tanstack/react-router'
import { useId, useState } from 'react'

import RouteErrorPage from '@/components/layout/RouteErrorPage'
import ScreenLayout from '@/components/layout/ScreenLayout'
import EmptyState from '@/components/ui/EmptyState'
import { stateActionClass } from '@/components/ui/empty-state-variants'
import MenuSingle from '@/components/ui/MenuSingle'
import RefreshRegion from '@/components/ui/RefreshRegion'
import SearchInput from '@/components/ui/SearchInput'
import SelectTrigger from '@/components/ui/SelectTrigger'
import TitleRow from '@/components/ui/TitleRow'
import UpdatingNote from '@/components/ui/UpdatingNote'
import { useBaskets } from '@/features/baskets/api/queries'
import BasketTile from '@/features/baskets/components/BasketTile'
import { AUDIENCE_LABELS } from '@/features/baskets/lib/audience'
import { sectionsOf, type BasketSection } from '@/features/baskets/lib/tags'
import { useOnline } from '@/hooks/useOnline'
import { lastUpdatedMeta, resolveViewState } from '@/lib/view-state'
import type { Audience, Basket } from '@/schemas/baskets'
import {
  BASKETS_SEARCH_DEFAULTS,
  basketsSearchSchema,
  loadBaskets,
} from '@/screens/baskets/BasketsScreen.loader'
import { BasketsListSkeleton } from '@/screens/baskets/components/BasketsSkeletons'
import { BASKET_GRID, BASKET_SEARCH } from '@/screens/baskets/components/basket-layout'

const TITLE = 'Baskets'

const HEADER = <TitleRow title={TITLE} />

/** The menu's no-tag row. A tag's slug is never empty, so it cannot collide. */
const ALL_TAGS = ''
const ALL_CATEGORIES = 'All categories'

/** What an audience with nothing published says, by how its baskets are picked. */
const NONE_YET: Record<Audience, { title: string; description: string }> = {
  investor: {
    title: 'No baskets for investors yet',
    description: 'Baskets will show here once our team publishes them.',
  },
  trader: {
    title: 'No baskets for traders yet',
    description: 'Trader baskets are rebuilt after each trading day’s close.',
  },
}

function BasketsPending() {
  return (
    <ScreenLayout pending pendingLabel="Loading baskets…" header={HEADER} pageTitle={TITLE}>
      <BasketsListSkeleton />
    </ScreenLayout>
  )
}

function BasketsError(props: ErrorComponentProps) {
  return <RouteErrorPage {...props} header={HEADER} title="Baskets did not load" />
}

export const Route = createFileRoute('/_app/baskets')({
  validateSearch: basketsSearchSchema,
  search: { middlewares: [stripSearchParams(BASKETS_SEARCH_DEFAULTS)] },
  loader: loadBaskets,
  pendingComponent: BasketsPending,
  errorComponent: BasketsError,
  component: BasketsScreen,
})

/** One tag's baskets under its name and count (mock `bka-group`). */
function BasketGroup({ section }: { section: BasketSection }) {
  const headId = useId()
  return (
    <section aria-labelledby={headId} className="flex flex-col gap-3">
      <div className="flex items-baseline justify-between gap-3">
        <h2 id={headId} className="text-h-section text-foreground">
          {section.title}
        </h2>
        <span className="text-body-xs text-muted-foreground-subtle">
          {section.baskets.length} {section.baskets.length === 1 ? 'basket' : 'baskets'}
        </span>
      </div>
      <ul className={BASKET_GRID} aria-label={section.title}>
        {section.baskets.map((basket) => (
          <li key={basket.slug} className="min-w-0">
            <BasketTile basket={basket} />
          </li>
        ))}
      </ul>
    </section>
  )
}

/** Name or tagline, case-blind. A symbol is not searchable: the list carries no members (ask B2). */
const matches = (basket: Basket, needle: string): boolean =>
  basket.name.toLowerCase().includes(needle) ||
  (basket.tagline?.toLowerCase().includes(needle) ?? false)

/**
 * Baskets (mock `qY`): For investors (picked by our team) or For traders (rebuilt by a rule after
 * every close), one page each, picked from the sidebar's two Baskets rows as the 2026-11-03 mock
 * does (`/baskets`, `/baskets?audience=trader`), which also draw as the strip over the page.
 * Then the mock's search and "All categories" menu, then one section per tag (the mock's
 * categories) of cards in staff's display order. The audience and the category are the URL's
 * (`?audience=&tag=`) and filtered by mm-be; the search over names and taglines narrows the cards
 * on screen and is the box's own.
 *
 * A category change keeps the cards on screen, marked as updating, until the new list lands. The
 * menu lists the audience's whole tag set, which stays put while one is picked; an audience change
 * is a new page, and drops the category, since the other audience's tags differ.
 */
function BasketsScreen() {
  const { audience, tag } = Route.useSearch()
  const navigate = Route.useNavigate()
  const online = useOnline()
  const query = useBaskets({ audience, tag })
  const view = resolveViewState(query, { online })
  const [term, setTerm] = useState('')
  const [groupsOpen, setGroupsOpen] = useState(false)

  const clear = () => {
    setTerm('')
  }
  const setTag = (value: string) => {
    void navigate({
      search: (prev) => ({ ...prev, tag: value === ALL_TAGS ? undefined : value }),
    })
  }

  const list = query.data
  const tags = list?.tags ?? []
  const needle = term.trim().toLowerCase()
  const baskets = list?.baskets ?? []
  const shown = needle === '' ? baskets : baskets.filter((basket) => matches(basket, needle))
  const groupOptions = [
    { value: ALL_TAGS, label: ALL_CATEGORIES },
    ...tags.map((option) => ({ value: option.slug, label: option.name })),
  ]
  const groupLabel = tags.find((option) => option.slug === tag)?.name ?? ALL_CATEGORIES
  // Nothing published for this audience: no search or chips over an empty page. The search row is
  // hidden, not unmounted, so its UpdatingNote stays a live region a screen reader is watching.
  const noneYet = view === 'loaded' && baskets.length === 0 && tag === undefined

  return (
    <ScreenLayout
      header={HEADER}
      pageTitle={`${TITLE}, ${AUDIENCE_LABELS[audience].toLowerCase()}`}
    >
      <div key={audience} className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center justify-between gap-3" hidden={noneYet}>
          <SearchInput
            aria-label="Find a basket"
            placeholder="Basket name or what it holds"
            value={term}
            onChange={(event) => {
              setTerm(event.target.value)
            }}
            onClear={clear}
            className={BASKET_SEARCH}
          />
          <div className="flex items-center gap-3 max-md:w-full max-md:justify-end">
            <UpdatingNote when={query.isPlaceholderData} label="Updating baskets…" />
            {tags.length === 0 ? null : (
              <MenuSingle
                options={groupOptions}
                value={tag ?? ALL_TAGS}
                onChange={setTag}
                open={groupsOpen}
                onOpenChange={setGroupsOpen}
                label="Category"
                searchable={false}
                align="end"
                trigger={(triggerProps) => (
                  <SelectTrigger
                    {...triggerProps}
                    aria-label={`Show a category, ${groupLabel}`}
                    label={groupLabel}
                    open={groupsOpen}
                    filled={tag !== undefined}
                  />
                )}
              />
            )}
          </div>
        </div>

        {view === 'failed' || view === 'offline' ? (
          <EmptyState
            kind={view}
            size="section"
            framed
            onRetry={() => void query.refetch()}
            retrying={query.isFetching}
            meta={view === 'failed' ? lastUpdatedMeta(query.dataUpdatedAt) : undefined}
            {...(view === 'failed' ? { title: 'Baskets did not load' } : {})}
          />
        ) : view === 'loading' ? (
          <BasketsListSkeleton cardsOnly />
        ) : (
          <RefreshRegion updating={query.isPlaceholderData}>
            {noneYet ? (
              <EmptyState kind="empty" size="section" framed {...NONE_YET[audience]} />
            ) : baskets.length === 0 ? (
              <EmptyState
                kind="empty"
                size="section"
                framed
                title="No basket in this category"
                description="Pick another category, or see them all."
                action={
                  <button
                    type="button"
                    className={stateActionClass('section')}
                    onClick={() => {
                      setTag(ALL_TAGS)
                    }}
                  >
                    Show all baskets
                  </button>
                }
              />
            ) : shown.length === 0 ? (
              <EmptyState
                kind="no-results"
                size="section"
                framed
                query={term}
                description="Try another word from a basket's name."
                action={
                  <button type="button" className={stateActionClass('section')} onClick={clear}>
                    Show all baskets
                  </button>
                }
              />
            ) : (
              <div className="flex flex-col gap-7">
                {sectionsOf(shown, tags, tag).map((section) => (
                  <BasketGroup key={section.key} section={section} />
                ))}
              </div>
            )}
          </RefreshRegion>
        )}
      </div>
    </ScreenLayout>
  )
}
